import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  makeStyles,
  Button,
  LinearProgress
} from '@material-ui/core';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PeopleIcon from '@material-ui/icons/People';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import WarningIcon from '@material-ui/icons/Warning';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ContactSupportIcon from '@material-ui/icons/ContactSupport';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
  },
  statCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: theme.spacing(2),
    height: '100%',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  statIcon: {
    height: 48,
    width: 48,
    backgroundColor: '#002D55',
    color: '#ffffff',
  },
  trendUp: {
    color: '#4caf50',
    fontWeight: 600,
  },
  trendDown: {
    color: '#f44336',
    fontWeight: 600,
  },
  sectionCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    marginTop: theme.spacing(2),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  cardHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #F79500',
  },
  healthChip: {
    fontWeight: 600,
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 600,
    color: '#002D55',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  filterBar: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  orangeButton: {
    backgroundColor: '#F79500',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#e69520',
    },
  },
  responsiveText: {
    fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)',
  },
  engagementBar: {
    height: 8,
    borderRadius: 4,
  },
}));

const CRMAdminDashboard = () => {
  const classes = useStyles();
  const token = useSelector((state) => state.auth.token);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [careProviders, setCareProviders] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [churnData, setChurnData] = useState(null);
  const [featureData, setFeatureData] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [healthFilter, setHealthFilter] = useState('all');
  const [sortBy, setSortBy] = useState('-engagement_score');

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchAllData();
    }
  }, [isLoggedIn, token]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchDashboard(),
        fetchCareProviders(),
        fetchRevenue(),
        fetchChurn(),
        fetchFeatures(),
        fetchSupportTickets(),
      ]);
    } catch (err) {
      setError(err.message || 'Failed to fetch CRM data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/admin/dashboard/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch dashboard');
      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  };

  const fetchCareProviders = async () => {
    try {
      const params = new URLSearchParams();
      if (healthFilter !== 'all') params.append('health_status', healthFilter);
      params.append('ordering', sortBy);

      const response = await fetch(`${backendBaseUrl}/api/crm/admin/analytics/providers/?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch care providers');
      const data = await response.json();
      setCareProviders(data.results || data);
    } catch (err) {
      console.error('Care providers fetch error:', err);
    }
  };

  const fetchRevenue = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/admin/analytics/revenue/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch revenue');
      const data = await response.json();
      setRevenueData(data);
    } catch (err) {
      console.error('Revenue fetch error:', err);
    }
  };

  const fetchChurn = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/admin/analytics/churn/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch churn data');
      const data = await response.json();
      setChurnData(data);
    } catch (err) {
      console.error('Churn fetch error:', err);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/admin/analytics/features/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch feature data');
      const data = await response.json();
      setFeatureData(data.features || []);
    } catch (err) {
      console.error('Feature fetch error:', err);
    }
  };

  const fetchSupportTickets = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/admin/support/tickets/?status=open`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch support tickets');
      const data = await response.json();
      setSupportTickets(data.results || data);
    } catch (err) {
      console.error('Support tickets fetch error:', err);
    }
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'active':
        return '#4caf50';
      case 'inactive_30d':
        return '#ff9800';
      case 'inactive_60d':
        return '#f44336';
      case 'at_risk':
        return '#d32f2f';
      case 'churned':
        return '#757575';
      default:
        return '#9e9e9e';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return '#d32f2f';
      case 'high':
        return '#f57c00';
      case 'medium':
        return '#fbc02d';
      case 'low':
        return '#689f38';
      default:
        return '#9e9e9e';
    }
  };

  const formatCurrency = (amount, currency = 'DKK') => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (!isLoggedIn || !token) {
    return (
      <Box p={3}>
        <Typography color="error">Please log in to view CRM dashboard</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={60} style={{ color: '#002D55' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error: {error}</Typography>
        <Button onClick={fetchAllData} variant="contained" className={classes.orangeButton} style={{ marginTop: 16 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Key Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="textSecondary" className={classes.responsiveText}>
                  Total Revenue
                </Typography>
                <Typography variant="h5" style={{ color: '#002D55', fontWeight: 600 }}>
                  {revenueData ? formatCurrency(revenueData.total_revenue) : '—'}
                </Typography>
                <Typography variant="caption" className={classes.trendUp}>
                  MRR: {revenueData ? formatCurrency(revenueData.total_mrr) : '—'}
                </Typography>
              </Box>
              <Avatar className={classes.statIcon}>
                <AttachMoneyIcon />
              </Avatar>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="textSecondary" className={classes.responsiveText}>
                  Active Clinics
                </Typography>
                <Typography variant="h5" style={{ color: '#002D55', fontWeight: 600 }}>
                  {dashboardData?.active_providers || 0}
                </Typography>
                <Typography variant="caption" className={classes.trendUp}>
                  +{dashboardData?.new_providers_7d || 0} this week
                </Typography>
              </Box>
              <Avatar className={classes.statIcon}>
                <PeopleIcon />
              </Avatar>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="textSecondary" className={classes.responsiveText}>
                  Conversion Rate
                </Typography>
                <Typography variant="h5" style={{ color: '#002D55', fontWeight: 600 }}>
                  {churnData?.conversion_rate ? `${churnData.conversion_rate.toFixed(1)}%` : '—'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {churnData?.converted || 0} / {churnData?.total_trials || 0} trials
                </Typography>
              </Box>
              <Avatar className={classes.statIcon}>
                <TrendingUpIcon />
              </Avatar>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="textSecondary" className={classes.responsiveText}>
                  At Risk
                </Typography>
                <Typography variant="h5" style={{ color: '#d32f2f', fontWeight: 600 }}>
                  {churnData?.at_risk_count || 0}
                </Typography>
                <Typography variant="caption" className={classes.trendDown}>
                  {churnData?.churned_count || 0} churned
                </Typography>
              </Box>
              <Avatar className={classes.statIcon} style={{ backgroundColor: '#f44336' }}>
                <WarningIcon />
              </Avatar>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card className={classes.filterBar} style={{ marginTop: 24 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Health Status"
              value={healthFilter}
              onChange={(e) => setHealthFilter(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.responsiveText}
            >
              <MenuItem value="all">All Providers</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive_30d">Inactive 30d</MenuItem>
              <MenuItem value="inactive_60d">Inactive 60d</MenuItem>
              <MenuItem value="at_risk">At Risk</MenuItem>
              <MenuItem value="churned">Churned</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Sort By"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.responsiveText}
            >
              <MenuItem value="-engagement_score">Engagement (High to Low)</MenuItem>
              <MenuItem value="engagement_score">Engagement (Low to High)</MenuItem>
              <MenuItem value="-total_revenue">Revenue (High to Low)</MenuItem>
              <MenuItem value="total_revenue">Revenue (Low to High)</MenuItem>
              <MenuItem value="-last_activity_date">Most Recent Activity</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              className={classes.orangeButton}
              onClick={fetchCareProviders}
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Care Providers Table */}
      <Card className={classes.sectionCard}>
        <CardHeader
          className={classes.cardHeader}
          avatar={
            <Avatar style={{ backgroundColor: '#002D55' }}>
              <AssessmentIcon />
            </Avatar>
          }
          title={<Typography variant="h6" style={{ color: '#002D55', fontWeight: 600 }}>Care Provider Analytics</Typography>}
        />
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>Clinic</TableCell>
                  <TableCell className={classes.tableHeader}>Health</TableCell>
                  <TableCell className={classes.tableHeader}>Engagement</TableCell>
                  <TableCell className={classes.tableHeader} align="right">Revenue</TableCell>
                  <TableCell className={classes.tableHeader} align="right">Patients</TableCell>
                  <TableCell className={classes.tableHeader} align="right">Staff</TableCell>
                  <TableCell className={classes.tableHeader}>Last Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {careProviders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary">No care providers found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  careProviders.map((provider) => (
                    <TableRow key={provider.id} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#002D55' }}>
                          {provider.care_provider_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={provider.health_status?.replace('_', ' ').toUpperCase()}
                          size="small"
                          className={classes.healthChip}
                          style={{
                            backgroundColor: getHealthColor(provider.health_status),
                            color: '#ffffff',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" style={{ fontWeight: 600, color: '#002D55' }}>
                            {provider.engagement_score || 0}/100
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={provider.engagement_score || 0}
                            className={classes.engagementBar}
                            style={{
                              backgroundColor: '#e0e0e0',
                            }}
                            classes={{
                              bar: classes.engagementBar,
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#002D55' }}>
                          {formatCurrency(provider.total_revenue || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{provider.total_patients || 0}</TableCell>
                      <TableCell align="right">{provider.total_staff || 0}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {provider.last_activity_date
                            ? new Date(provider.last_activity_date).toLocaleDateString('da-DK')
                            : 'Never'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Feature Adoption */}
      <Card className={classes.sectionCard}>
        <CardHeader
          className={classes.cardHeader}
          avatar={
            <Avatar style={{ backgroundColor: '#002D55' }}>
              <TrendingUpIcon />
            </Avatar>
          }
          title={<Typography variant="h6" style={{ color: '#002D55', fontWeight: 600 }}>Feature Adoption</Typography>}
        />
        <CardContent>
          <Grid container spacing={2}>
            {featureData.length === 0 ? (
              <Grid item xs={12}>
                <Typography color="textSecondary" align="center">No feature data available</Typography>
              </Grid>
            ) : (
              featureData.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                    <Typography variant="body2" style={{ color: '#002D55', fontWeight: 600 }}>
                      {feature.feature_name}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#f5a62f', fontWeight: 600 }}>
                      {feature.adoption_rate ? `${feature.adoption_rate.toFixed(1)}%` : '0%'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {feature.total_providers_using || 0} clinics using
                    </Typography>
                  </Box>
                </Grid>
              ))
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Open Support Tickets */}
      <Card className={classes.sectionCard}>
        <CardHeader
          className={classes.cardHeader}
          avatar={
            <Avatar style={{ backgroundColor: '#002D55' }}>
              <ContactSupportIcon />
            </Avatar>
          }
          title={<Typography variant="h6" style={{ color: '#002D55', fontWeight: 600 }}>Open Support Tickets</Typography>}
        />
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>Clinic</TableCell>
                  <TableCell className={classes.tableHeader}>Subject</TableCell>
                  <TableCell className={classes.tableHeader}>Priority</TableCell>
                  <TableCell className={classes.tableHeader}>Category</TableCell>
                  <TableCell className={classes.tableHeader}>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supportTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary">No open tickets</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  supportTickets.slice(0, 10).map((ticket) => (
                    <TableRow key={ticket.id} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#002D55' }}>
                          {ticket.care_provider_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{ticket.subject}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.priority?.toUpperCase()}
                          size="small"
                          style={{
                            backgroundColor: getPriorityColor(ticket.priority),
                            color: '#ffffff',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {ticket.category}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(ticket.created_at).toLocaleDateString('da-DK')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CRMAdminDashboard;
